import appContext from "../../appContext";
import CustomLoader from "../../components/Custom/CustomLoader";
import CustomizedInput from "../../components/CreateForm/InputBase";
import CustomizedSingleSelectDropdown from "../../components/CreateForm/DropdownBase";

import { sortOptions } from "../../Utils/common";
import { getCardLastEditedOn } from "../Workspace/sidebarutils";
import { getUserTemplatesFromBuilder } from "../../Services/API";
import { TemplateCardContainer } from "../Workspace/CardContainer";

import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  TemplateActionContainer,
  TemplateActionGridItem,
  TemplateLabelContainer,
  TemplateListContainer,
  TemplateListEmptyContainer,
  TemplateListGridContainer,
  TemplateRootContainer,
} from "./styled";

interface UserTemplatesProps {
  selectedTemplateId: string;
  setSelectedTemplateId: (id: string) => void;
}

function UserTemplates({
  selectedTemplateId,
  setSelectedTemplateId,
}: UserTemplatesProps) {
  const { auth } = useContext(appContext);

  const [limit, setLimit] = useState<number>(20);
  const [sort, setSort] = useState<string>("lastModified");
  const [templates, setTemplates] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const getTemplates = useCallback(async () => {
    try {
      setLoading(true);

      if (auth) {
        const userTemplates = await getUserTemplatesFromBuilder(
          auth,
          sort,
          limit
        );

        const selectedId = userTemplates?.published?.template.length
          ? userTemplates.published.template[0]?.id
          : "";

        setTemplates(userTemplates);
        if(!selectedTemplateId) {
          setSelectedTemplateId(selectedId);
        }
      }
      setLoading(false);
    } catch (e: any) {
      setTemplates([]);
      setLoading(false);
      console.error(e.message || e);
    }
  }, [auth, sort, limit, setSelectedTemplateId]);

  useEffect(() => {
    getTemplates();
  }, [getTemplates, limit, sort]);

  const renderList = useMemo(() => {
    if (loading) {
      return <CustomLoader />;
    }

    const publishedTemplates = templates?.published?.template
      ? templates?.published?.template
      : [];

    if (publishedTemplates.length) {
      return (
        <TemplateListGridContainer>
          {publishedTemplates.map((template: any, indexKey: number) => {
            return (
              <TemplateCardContainer
                indexKey={indexKey}
                cardImage={template?.settings?.social?.image}
                cardLastEditedOn={getCardLastEditedOn(template?.lastModified)}
                cardTitle={template?.name}
                isSelected={selectedTemplateId === template?.id}
                selectHandler={() => {
                  setSelectedTemplateId(template?.id);
                }}
              />
            );
          })}
        </TemplateListGridContainer>
      );
    }

    return (
      <TemplateListEmptyContainer>
        <div>Nothing to show</div>
        <div>Please navigate to Landing page to create a new template</div>
      </TemplateListEmptyContainer>
    );
  }, [
    loading,
    selectedTemplateId,
    setSelectedTemplateId,
    templates?.published?.template,
  ]);

  return (
    <TemplateRootContainer>
      <TemplateActionContainer container>
        <TemplateActionGridItem item xs={6}>
          <TemplateLabelContainer>Sort By</TemplateLabelContainer>
          <CustomizedSingleSelectDropdown
            value={sort}
            setValue={setSort}
            dataProvider={sortOptions}
          />
        </TemplateActionGridItem>
        <TemplateActionGridItem item xs={6}>
          <TemplateLabelContainer>Limit</TemplateLabelContainer>
          <CustomizedInput
            type="number"
            value={limit}
            placeholder="Type here"
            InputProps={{ inputProps: { min: 0, max: 50 } }}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              setLimit(Number(event.target.value));
            }}
          />
        </TemplateActionGridItem>
      </TemplateActionContainer>
      <TemplateListContainer>{renderList}</TemplateListContainer>
    </TemplateRootContainer>
  );
}

export default UserTemplates;
